"use client"

import { useEffect, useState } from "react"
import API from "../services/api"
import { io } from "socket.io-client"
import "./LoisirsClient.css"

const socket = io("https://server-deploy-aq8t.onrender.com")

// Translation system
const translations = {
  fr: {
    // Header
    back: "Retour",

    // Welcome banner
    servicesAndLeisure: "Services & Loisirs",
    discoverActivities: "Découvrez nos activités et divertissements pour tous les âges",

    // Loading & Empty states
    loadingServices: "Chargement des services...",
    noServiceFound: "Aucun service trouvé",
    comeBackSoonActivities: "Revenez bientôt pour découvrir nos activités",

    // Detail view
    backToList: "Retour à la liste",
    noDescriptionAvailable: "Pas de description disponible.",
    openingHours: "Horaires d'ouverture",
    opening: "Ouverture",
    closing: "Fermeture",
    activitySupervised: "Activité encadrée",
    equipmentProvided: "Matériel fourni",
    forAllLevels: "Pour tous niveaux",
    reservationRecommended: "Réservation recommandée",
    viewDetails: "Voir détails",

    // Modal
    reserveActivity: "Réserver une activité",
    name: "Nom",
    numberOfPeople: "Nombre de personnes",
    reserve: "Réserver",
    cancel: "Annuler",
    reservationSuccess: "Réservation créée avec succès !",
    reservationError: "Erreur lors de la création de la réservation.",
    undefined: "Non défini",

    // Footer (re-use from previous components if consistent)
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Disponible à la réception",
    addressLine1: "Avenue Mohamed V",
    addressLine2: "Tunis, Tunisie",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
  },
  en: {
    // Header
    back: "Back",

    // Welcome banner
    servicesAndLeisure: "Services & Leisure",
    discoverActivities: "Discover our activities and entertainment for all ages",

    // Loading & Empty states
    loadingServices: "Loading services...",
    noServiceFound: "No service found",
    comeBackSoonActivities: "Come back soon to discover our activities",

    // Detail view
    backToList: "Back to list",
    noDescriptionAvailable: "No description available.",
    openingHours: "Opening Hours",
    opening: "Opening",
    closing: "Closing",
    activitySupervised: "Supervised activity",
    equipmentProvided: "Equipment provided",
    forAllLevels: "For all levels",
    reservationRecommended: "Reservation recommended",
    viewDetails: "View details",

    // Modal
    reserveActivity: "Reserve an Activity",
    name: "Name",
    numberOfPeople: "Number of People",
    reserve: "Reserve",
    cancel: "Cancel",
    reservationSuccess: "Reservation created successfully!",
    reservationError: "Error creating reservation.",
    undefined: "Undefined",

    // Footer
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "Available at reception",
    addressLine1: "Avenue Mohamed V",
    addressLine2: "1002 Tunis, Tunisia",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
  },
  ar: {
    // Header
    back: "رجوع",

    // Welcome banner
    servicesAndLeisure: "الخدمات والترفيه",
    discoverActivities: "اكتشف أنشطتنا ووسائل الترفيه لجميع الأعمار",

    // Loading & Empty states
    loadingServices: "جاري تحميل الخدمات...",
    noServiceFound: "لم يتم العثور على خدمة",
    comeBackSoonActivities: "عد قريبًا لاكتشاف أنشطتنا",

    // Detail view
    backToList: "العودة إلى القائمة",
    noDescriptionAvailable: "لا يوجد وصف متاح.",
    openingHours: "ساعات العمل",
    opening: "الافتتاح",
    closing: "الإغلاق",
    activitySupervised: "نشاط بإشراف",
    equipmentProvided: "المعدات متوفرة",
    forAllLevels: "لجميع المستويات",
    reservationRecommended: "يوصى بالحجز",
    viewDetails: "عرض التفاصيل",

    // Modal
    reserveActivity: "حجز نشاط",
    name: "الاسم",
    numberOfPeople: "عدد الأشخاص",
    reserve: "حجز",
    cancel: "إلغاء",
    reservationSuccess: "تم إنشاء الحجز بنجاح!",
    reservationError: "حدث خطأ أثناء إنشاء الحجز.",
    undefined: "غير محدد",

    // Footer
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "متوفرة في الاستقبال",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/SolutionQrCode/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/SolutionQrCode/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/SolutionQrCode/images/ar-flag-v2.png" },
]

const LoisirsClient = () => {
  const [loisirs, setLoisirs] = useState([])
  const [selectedLoisir, setSelectedLoisir] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [reservationData, setReservationData] = useState({
    name: "",
    from: "",
    to: new Date().toISOString().split("T")[0], // today's date
    people: 1,
  })
  const [currentLanguage, setCurrentLanguage] = useState("fr")
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)

  // Get translation function
  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  // Function to format time from datetime
  const formatTimeOnly = (dateString) => {
    if (!dateString) return t("undefined")
    const date = new Date(dateString)
    const locale = currentLanguage === "ar" ? "ar-TN" : currentLanguage === "en" ? "en-US" : "fr-FR"
    return date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
  }

  const handleReservationSubmit = async () => {
    try {
      await API.post("/reservations", reservationData)
      alert(t("reservationSuccess"))
      setShowModal(false)
      setReservationData({ name: "", from: "", to: "", people: 1 })
    } catch (error) {
      console.error("Erreur de réservation:", error)
      alert(t("reservationError"))
    }
  }

  const fetchLoisirs = async () => {
    try {
      setIsLoading(true)
      const res = await API.get("/loisirs")
      setLoisirs(res.data)
      setIsLoaded(true)
    } catch (error) {
      console.error("Erreur chargement des loisirs:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchLoisirs()
    window.scrollTo(0, 0) // Scroll to the top of the page on mount

    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem("novotel-language")
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      // Update document direction for Arabic
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }

    // Force a re-render after a short delay to ensure visibility
    const timer = setTimeout(() => {
      setIsLoaded(true)
    }, 100)
    socket.on("loisirCreated", (newLoisir) => {
      setLoisirs((prev) => [...prev, newLoisir])
    })
    socket.on("loisirUpdated", (updatedLoisir) => {
      setLoisirs((prev) => prev.map((l) => (l._id === updatedLoisir._id ? updatedLoisir : l)))
    })
    socket.on("loisirDeleted", (deletedId) => {
      setLoisirs((prev) => prev.filter((l) => l._id !== deletedId))
    })
    return () => {
      socket.off("loisirCreated")
      socket.off("loisirUpdated")
      socket.off("loisirDeleted")
      clearTimeout(timer)
    }
  }, [])

  // Language change handler
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("novotel-language", langCode)
    setShowLanguageDropdown(false)

    // Update document direction for Arabic
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  return (
    <div className={`hotel-app4 ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <style jsx>{`
        /* Language dropdown styles */
        .language-selector {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 20;
        }
        
        .language-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .language-toggle:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }
        
        .language-flag {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }
        
        .language-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          background: rgba(0, 0, 0, 0.9);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 150px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          margin-top: 5px;
        }
        
        .language-option {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
          gap: 8px;
          color: white;
          font-size: 14px;
        }
        
        .language-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .language-option.active {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .flag-small {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        /* RTL Support */
        .rtl {
          direction: rtl;
        }

        .rtl .language-selector {
          left: auto;
          right: 15px;
        }

        .rtl .language-dropdown {
          left: auto;
          right: 0;
          text-align: right;
        }

        .rtl .header-back-link {
          flex-direction: row-reverse;
        }

        .rtl .header-back-link svg {
          margin-left: 8px;
          margin-right: 0;
        }

        .rtl .welcome-banner h1,
        .rtl .welcome-banner p {
          text-align: right;
        }

        .rtl .loading-container p,
        .rtl .empty-state h3,
        .rtl .empty-state p {
          text-align: right;
        }

        .rtl .spa-detail-view .back-to-list {
          flex-direction: row-reverse;
        }

        .rtl .spa-detail-view .back-to-list svg {
          margin-left: 8px;
          margin-right: 0;
        }

        .rtl .spa-detail-info {
          text-align: right;
        }

        .rtl .detail-time-title {
          flex-direction: row-reverse;
        }

        .rtl .detail-time-title .time-icon {
          margin-left: 8px;
          margin-right: 0;
        }

        .rtl .detail-time-content {
          flex-direction: row-reverse;
        }

        .rtl .detail-time-item {
          text-align: center; /* Keep content centered within the item */
        }

        .rtl .spa-features {
          text-align: right;
        }

        .rtl .spa-feature-item {
          flex-direction: row-reverse;
        }

        .rtl .spa-feature-item .feature-icon {
          margin-left: 8px;
          margin-right: 0;
        }

        .rtl .content-item-content {
          text-align: right;
        }

        .rtl .content-item-arrow {
          transform: scaleX(-1); /* Flip arrow for RTL */
        }

        .rtl .content-item-overlay {
          direction: rtl; /* Ensure overlay text is correct */
        }

        .rtl .modal h2,
        .rtl .modal input,
        .rtl .modal-actions {
          text-align: right;
        }

        .rtl .modal-actions button {
          margin-left: 10px;
          margin-right: 0;
        }

        .rtl .footer-section {
          text-align: right;
        }

        .rtl .copyright {
          text-align: right;
        }

        .rtl .copyright a {
          margin-left: 0;
          margin-right: 5px;
        }

        .time-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 12px;
          padding: 8px 12px;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-radius: 8px;
          border-left: 4px solid #3b82f6;
          font-size: 0.875rem;
        }
        .time-item {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #475569;
          font-weight: 500;
        }
        .time-icon {
          font-size: 1rem;
          color: #3b82f6;
        }
        .time-value {
          color: #1e293b;
          font-weight: 600;
          font-family: 'Courier New', monospace;
          background: #ffffff;
          padding: 2px 6px;
          border-radius: 4px;
          border: 1px solid #e2e8f0;
        }
        .time-separator {
          color: #94a3b8;
          font-weight: 400;
          margin: 0 4px;
        }
        .detail-time-info {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          border-radius: 12px;
          padding: 16px;
          margin: 20px 0;
          border-left: 5px solid #3b82f6;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        .detail-time-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .detail-time-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .detail-time-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 12px 16px;
          background: #ffffff;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          min-width: 120px;
        }
        .detail-time-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .detail-time-value {
          font-size: 1.25rem;
          color: #1e293b;
          font-weight: 700;
          font-family: 'Courier New', monospace;
        }
        .time-status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .status-open {
          background: #dcfce7;
          color: #166534;
        }
        .status-closed {
          background: #fef2f2;
          color: #991b1b;
        }
        .status-unknown {
          background: #f1f5f9;
          color: #475569;
        }
        @media (max-width: 768px) {
          .time-info {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
          }
          .time-item {
            justify-content: space-between;
          }
          .detail-time-content {
            flex-direction: column;
            gap: 12px;
          }
          .detail-time-item {
            min-width: 100%;
          }
        }
      `}</style>
      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <header className="app-header">
        <button
          className="header-back-link"
          onClick={() => {
            if (selectedLoisir) {
              setSelectedLoisir(null)
            } else {
              window.location.href = "/SolutionQrCode/Home"
            }
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("back")}
        </button>
        <div className="logo-container">
          <img src="/SolutionQrCode/images/logo2.png" alt="Novotel Logo" className="logo" />
        </div>
        <div></div> {/* Empty div for flex spacing */}
      </header>
      <main className="app-main">
        <div className="welcome-banner">
          <h1>
            <span>{t("servicesAndLeisure")}</span> 
          </h1>
          <p>{t("discoverActivities")}</p>
        </div>
        <div className="content-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t("loadingServices")}</p>
            </div>
          ) : loisirs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>{t("noServiceFound")}</h3>
              <p>{t("comeBackSoonActivities")}</p>
            </div>
          ) : selectedLoisir ? (
            <div className="spa-detail-view">
              <button className="back-to-list" onClick={() => setSelectedLoisir(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t("backToList")}
              </button>
              <div className="spa-detail-content">
                <div className="spa-detail-image-container">
                  <img
                    src={selectedLoisir.image || "/placeholder.svg"}
                    alt={selectedLoisir.name}
                    className="spa-detail-image"
                    onError={(e) => {
                      e.target.src = `/placeholder.svg?height=300&width=500&text=${selectedLoisir.name}`
                    }}
                  />
                </div>
                <div className="spa-detail-info">
                  <h2 className="spa-detail-name">{selectedLoisir.name}</h2>
                  <div className="spa-detail-description">
                    <p>{selectedLoisir.description || t("noDescriptionAvailable")}</p>
                  </div>
                  <div className="detail-time-info">
                    <div className="detail-time-title">
                      <span className="time-icon">🕒</span>
                      {t("openingHours")}
                    </div>
                    <div className="detail-time-content">
                      <div className="detail-time-item">
                        <span className="detail-time-label">{t("opening")}</span>
                        <span className="detail-time-value">{formatTimeOnly(selectedLoisir.ouverture)}</span>
                      </div>
                      <div className="detail-time-item">
                        <span className="detail-time-label">{t("closing")}</span>
                        <span className="detail-time-value">{formatTimeOnly(selectedLoisir.fermeture)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="spa-features">
                    <div className="spa-feature-item">
                      <span className="feature-icon">✓</span>
                      <span>{t("activitySupervised")}</span>
                    </div>
                    <div className="spa-feature-item">
                      <span className="feature-icon">✓</span>
                      <span>{t("equipmentProvided")}</span>
                    </div>
                    <div className="spa-feature-item">
                      <span className="feature-icon">✓</span>
                      <span>{t("forAllLevels")}</span>
                    </div>
                    <div className="spa-feature-item">
                      <span className="feature-icon">✓</span>
                      <span>{t("reservationRecommended")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`content-grid ${isLoaded ? "loaded" : ""}`}>
              {loisirs.map((loisir, index) => (
                <div
                  key={loisir._id}
                  className="content-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => setSelectedLoisir(loisir)}
                >
                  <div className="content-item-image">
                    <img
                      src={loisir.image || "/placeholder.svg"}
                      alt={loisir.name}
                      onError={(e) => {
                        e.target.src = `/placeholder.svg?height=120&width=300&text=${loisir.name}`
                      }}
                    />
                    <div className="content-item-overlay">
                      <span className="view-details">{t("viewDetails")}</span>
                    </div>
                  </div>
                  <div className="content-item-content">
                    <h3>{loisir.name}</h3>
                    <p>{loisir.description}</p>
                    <div className="content-item-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12 4L20 12L12 20M4 12H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {showModal && (
          <div className="modal-backdrop">
            <div className="modal">
              <h2>{t("reserveActivity")}</h2>
              <input
                type="text"
                placeholder={t("name")}
                value={reservationData.name}
                onChange={(e) => setReservationData({ ...reservationData, name: e.target.value })}
                required
              />
              <input
                type="datetime-local"
                value={reservationData.from}
                onChange={(e) => setReservationData({ ...reservationData, from: e.target.value })}
                required
              />
              <input
                type="number"
                min="1"
                placeholder={t("numberOfPeople")}
                value={reservationData.people}
                onChange={(e) => setReservationData({ ...reservationData, people: e.target.value })}
                required
              />
              <div className="modal-actions">
                <button onClick={handleReservationSubmit}>✅ {t("reserve")}</button>
                <button onClick={() => setShowModal(false)}>❌ {t("cancel")}</button>
              </div>
            </div>
          </div>
        )}
      </main>
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 832 555</p>
            <p>H6145@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 832 555</p>
            <p>H6145@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>
             
            <p>
              {t("password")}: {t("availableAtReception")}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/NovotelTunis" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/novotel.tunis" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a href="https://x.com/novoteltunis" aria-label="Twitter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} Novotel Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/SolutionQrCode/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default LoisirsClient
